import { BadgeCheck, DatabaseZap, Info, Lock, Palette, ShieldCheck, User } from "lucide-react";
import { useState } from "react";
import { useStore } from "../../../store/store";
import { cn } from "../../../utils";
import { Dialog, DialogContent, DialogTitle } from "../Dialog";
import { AboutSettings } from "./AboutSettings";
import { AppearanceSettings } from "./AppearanceSettings";
import { ConsentSettings } from "./ConsentSettings";
import { DataPrivacySettings } from "./DataPrivacySettings";
import { ProfileSettings } from "./ProfileSettings";
import { SecuritySettings } from "./SecuritySettings";
import { VerificationSettings } from "./VerificationSettings";

type TSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type TSettingsSection =
  | "profile"
  | "verification"
  | "security"
  | "consents"
  | "data"
  | "appearance"
  | "about";

type TNavItem = {
  id: TSettingsSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

type TNavGroup = {
  title: string;
  items: TNavItem[];
};

// Domain-grouped navigation (enterprise IA). Only sections wired to real
// functionality are listed here; new sub-items are added as backends land.
const NAV_GROUPS: TNavGroup[] = [
  { title: "Account", items: [{ id: "profile", label: "Profile", icon: User }] },
  { title: "Verification", items: [{ id: "verification", label: "Email & Phone", icon: BadgeCheck }] },
  { title: "Security", items: [{ id: "security", label: "Password & 2FA", icon: Lock }] },
  { title: "Privacy", items: [{ id: "consents", label: "Communication & Cookies", icon: ShieldCheck }] },
  { title: "Data & Privacy", items: [{ id: "data", label: "Your Data", icon: DatabaseZap }] },
  { title: "Appearance", items: [{ id: "appearance", label: "Theme", icon: Palette }] },
  { title: "About", items: [{ id: "about", label: "Legal & Support", icon: Info }] },
];

export const SettingsModal = ({ open, onOpenChange }: TSettingsModalProps) => {
  const [activeSection, setActiveSection] = useState<TSettingsSection>("profile");
  const profile = useStore.use.profile();

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return <ProfileSettings userData={profile ?? undefined} />;
      case "verification":
        return <VerificationSettings />;
      case "security":
        return <SecuritySettings />;
      case "consents":
        return <ConsentSettings />;
      case "data":
        return <DataPrivacySettings />;
      case "appearance":
        return <AppearanceSettings />;
      case "about":
        return <AboutSettings />;
      default:
        return null;
    }
  };

  const renderNavItem = (item: TNavItem) => {
    const Icon = item.icon;
    const isActive = activeSection === item.id;
    return (
      <button
        key={item.id}
        onClick={() => setActiveSection(item.id)}
        aria-current={isActive ? "page" : undefined}
        title={item.label}
        className={cn(
          "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:w-full",
          isActive
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline">{item.label}</span>
      </button>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden p-0 sm:h-[80vh] sm:flex-row [&>button]:z-10">
        <DialogTitle className="sr-only">Settings</DialogTitle>

        {/* Sidebar — horizontal scrollable tabs on mobile, grouped vertical sidebar on sm+ */}
        <div className="flex shrink-0 flex-row overflow-x-auto border-b bg-muted/30 p-2 sm:w-60 sm:flex-col sm:overflow-x-visible sm:overflow-y-auto sm:border-b-0 sm:border-r sm:p-4">
          <div className="mb-6 hidden sm:block">
            <h2 className="text-lg font-semibold">Settings</h2>
          </div>
          <nav className="flex flex-row gap-1 sm:flex-col sm:gap-4">
            {NAV_GROUPS.map((group) => (
              <div
                key={group.title}
                className="flex flex-row gap-1 sm:flex-col sm:gap-1"
              >
                <h3 className="hidden px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 sm:block">
                  {group.title}
                </h3>
                {group.items.map(renderNavItem)}
              </div>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
};
